import React from 'react';
import { Text } from 'ink';
import type { GitLabRemoteInfo } from '../../types';

interface ProjectIndicatorProps {
  gitlabRemoteInfo: GitLabRemoteInfo;
  cwd?: string;
}

/**
 * Produces a short, human-readable path showing only the last two segments,
 * e.g. `.../editor-extensions/gitlab-lsp`. Paths with two or fewer segments
 * are returned as-is.
 */
export function formatShortPath(fullPath: string): string {
  const sep = fullPath.includes('/') ? '/' : '\\';
  const parts = fullPath.split(sep).filter(Boolean);
  if (parts.length <= 2) return fullPath;
  return `...${sep}${parts.slice(-2).join(sep)}`;
}

/**
 * Shows the detected project path (short form) in the status bar, or
 * `(no project)` when no project is detected.
 */
export const ProjectIndicator: React.FC<ProjectIndicatorProps> = React.memo(
  ({ gitlabRemoteInfo, cwd }) => {
    if (gitlabRemoteInfo.status === 'connected') {
      const path = formatShortPath(gitlabRemoteInfo.gitlabPath.replace(/^\//, ''));
      return <Text dimColor>{path}</Text>;
    }

    // Fall back to the cwd short form when no GitLab remote is detected.
    if (cwd) {
      return <Text dimColor>{formatShortPath(cwd)}</Text>;
    }

    return <Text dimColor>(no project)</Text>;
  },
);
